import { ChatLauncherButton } from './ChatLauncherButton/ChatLauncherButton'
import { ChatWidgetProvider, useChatWidget } from './ChatWidget.context'
import { ConversationSocketProvider } from 'utils/conversationSocket'
import { ConversationsDrawer } from './ConversationsDrawer/ConversationsDrawer'
import { FC, useEffect, useMemo } from 'react'
import { useGetConversations, usePrefetchMessages } from 'dataQueries/conversations.query'

import Box from '@mui/material/Box'
import { CHAT_WIDGET_BOTTOM_VAR, CHAT_WIDGET_RIGHT_VAR, resolveLauncherPlacement } from './helpers/chatWidgetLayout'
import { NewConversation } from './NewConversation/NewConversation'
import { useBottomActionBarClearance } from 'utils/hooks/bottomActionBarRegistry'
import { useRightRail, useRightRailFooterClearance } from 'utils/hooks/rightRailRegistry'
import { useConversationsRealtime } from './helpers/useConversationsRealtime'
import { useDocumentTitleBadge } from './helpers/useDocumentTitleBadge'
import { useUnreadNotifications } from './helpers/useUnreadNotifications'

const FLOATING_CONTAINER_SX = {
  position: 'fixed',
  zIndex: 1300,
  transition: 'bottom 0.2s ease-out',
} as const

/**
 * Inner widget: owns realtime wiring and renders the launcher / reduced panel / expanded drawer.
 *
 * Shown to every eligible user under the feature flag (ISSUE-2345): the primary entry is a context-free
 * Client/Creative↔Ops support conversation, which needs no counterparty, so the former "at least one paired
 * creative" availability gate no longer applies.
 */
const ChatWidgetContent: FC = () => {
  const { mode } = useChatWidget()
  const bottomActionBarClearance = useBottomActionBarClearance()
  const rail = useRightRail()
  const railFooterClearance = useRightRailFooterClearance()
  const { right, bottom } = resolveLauncherPlacement({ rail, bottomActionBarClearance, railFooterClearance })

  // Fetch every conversation to drive the unread badge and the real-time subscriptions.
  const { data, isFetched } = useGetConversations()
  const conversations = useMemo(() => data?.content ?? [], [data])
  const conversationIds = useMemo(() => conversations.map((conversation) => conversation.id), [conversations])
  const unreadTotal = useMemo(
    () => conversations.reduce((total, conversation) => total + (conversation.unreadCount ?? 0), 0),
    [conversations]
  )

  useConversationsRealtime(conversationIds)

  // Surface the unread total in the browser tab title (e.g. "(3) Backbone") so it's visible even when
  // the tab is in the background and the floating launcher dot is off-screen.
  useDocumentTitleBadge(unreadTotal)

  // Raise a desktop notification (when the tab is hidden) whenever a conversation's unread count rises,
  // including the first message of a brand-new conversation. Driven off the list so it's race-free.
  useUnreadNotifications(conversations, isFetched)

  // Eagerly load the most recent conversation's message history as soon as the list lands so
  // opening the thread is instantaneous. We deliberately prefetch only the head — prefetching
  // every conversation would burn quota for users with long histories who may only chat with the
  // most recent counterparty.
  const prefetchMessages = usePrefetchMessages()
  useEffect(() => {
    if (conversations.length === 0) return
    prefetchMessages(conversations[0].id)
  }, [conversations, prefetchMessages])

  return (
    <>

      <Box
        sx={{
          ...FLOATING_CONTAINER_SX,
          right,
          bottom,
          [CHAT_WIDGET_RIGHT_VAR]: right,
          [CHAT_WIDGET_BOTTOM_VAR]: bottom,
        }}
      >

        {mode === 'closed' && <ChatLauncherButton unreadTotal={unreadTotal} />}

        {mode === 'reduced' && <NewConversation />}

      </Box>

      <ConversationsDrawer />

    </>
  )
}

/** Global chat widget. Mount once in the authenticated app shell behind the feature flag. */
export const ChatWidget: FC = () => {
  return (
    <ConversationSocketProvider>
      <ChatWidgetProvider>
        <ChatWidgetContent />
      </ChatWidgetProvider>
    </ConversationSocketProvider>
  )
}
